import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Empty, Input, Modal, Skeleton, Tooltip, message } from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileAddOutlined,
  FolderOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  LockOutlined,
  PlusOutlined,
} from '@ant-design/icons';

import { CategoryAccessDialog } from '../components/CategoryAccessDialog';
import { authApi } from '../lib/authApi';
import { editorApi, wikiAdminApi, wikiApi, type ArticlesResponse, type WikiArticleListItem } from '../lib/wikiApi';
import { kbShortDate, type KbArticleListItem, type KbBlock } from '../lib/api/kb';
import { MATERIAL_LABELS, TYPE_CHIPS, materialIcon, renderSectionIcon, sectionAccent, sectionSoft } from '../lib/sectionIcons';
import { makeSectionComparator } from '../lib/sectionOrder';
import { NewArticleModal } from '../components/NewArticleModal';
import { Button, Chip, Tag, fmtInt, plural, type TagColor } from '../components/ds';
import './KnowledgeBase.css';

/** MIME-тип HTML5 DnD для переноса статьи (несёт её id между dragstart и drop). */
const DND_MIME = 'application/x-wiki-article';
/** MIME-тип для переноса подраздела (несёт имя категории). */
const DND_CAT_MIME = 'application/x-wiki-category';

/**
 * Страница раздела базы знаний (HUB 2.0, прототип wk6/PageKnowledge «block»).
 *
 * Шапка: «← База знаний», плитка-иконка цвета раздела, название, описание и
 * число статей; справа «Доступ» (kb:admin, к выбранному подразделу),
 * «Порядок» (режим перетаскивания) и «Добавить статью».
 * Слева — подразделы с числом статей («Все статьи» + категории, «+ Подраздел»),
 * справа — чипы типов и карточки статей (статус, владелец, обновлено,
 * просмотры). Режим порядка: статьи перетаскиваются внутри подраздела и на
 * подраздел слева (перенос), подразделы — между собой. Порядок — sortOrder
 * (listArticles sort='order'), сохраняется сразу.
 */
export function BlockPage(): JSX.Element {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const meQuery = useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 });
  const blocksQuery = useQuery({ queryKey: ['wiki', 'blocks'], queryFn: () => wikiApi.listBlocks() });
  // Запрашиваем с sort='order' → бэк сортирует по sortOrder asc (порядок drag'а
  // переживает рефетч/F5). Дефолтная сортировка listArticles (publishedAt desc)
  // не тронута — её используют лендинг и пр.
  const articlesKey = ['wiki', 'articles', { blockCode: code, sort: 'order' }] as const;
  const articlesQuery = useQuery({
    queryKey: articlesKey,
    queryFn: () => wikiApi.listArticles({ blockCode: code, limit: 200, sort: 'order' }),
    enabled: !!code,
  });

  const block = blocksQuery.data?.find((b) => b.code === code);
  const canCreate = meQuery.data?.canCreate ?? false;
  // Тумблер «Режим редактирования» доступен любому редактору раздела.
  const canEditMode =
    (meQuery.data?.canEdit ?? false) || canCreate || (meQuery.data?.canAdmin ?? false);
  // Выкл по умолчанию: при ВКЛ показываем drag-ручки в дереве + инлайн-создание.
  const [editMode, setEditMode] = useState(false);

  // Подразделы (категории) блока — включая ПУСТЫЕ (для structure-first).
  const categoriesQuery = useQuery({
    queryKey: ['wiki', 'categories', code],
    queryFn: () => wikiApi.listCategories(code),
    enabled: !!code,
  });
  // Имя категории → {id, sortOrder, visibleToRoles} для порядка секций,
  // удаления и управления доступом по ролям.
  const catMeta = useMemo(() => {
    const m = new Map<string, { id: string; sortOrder: number; visibleToRoles: string[] }>();
    // Одно имя может встретиться у нескольких категорий (исторические дубли) —
    // берём минимальный sortOrder как «позицию» группы и любой id.
    for (const c of categoriesQuery.data ?? []) {
      const prev = m.get(c.name);
      if (!prev || c.sortOrder < prev.sortOrder) {
        m.set(c.name, { id: c.id, sortOrder: c.sortOrder, visibleToRoles: c.visibleToRoles ?? [] });
      }
    }
    return m;
  }, [categoriesQuery.data]);
  // Карта имя → sortOrder для ручного (drag) порядка секций.
  const catOrder = useMemo(() => {
    const m = new Map<string, number>();
    for (const [name, meta] of catMeta) m.set(name, meta.sortOrder);
    return m;
  }, [catMeta]);
  const sectionCmp = useMemo(() => makeSectionComparator(catOrder), [catOrder]);

  // Группируем статьи по под-разделам (категориям) → секции. Пустые категории
  // тоже показываем (засеваем из categoriesQuery). Порядок секций — по
  // sortOrder категории (как создавали), «Основные статьи» (__root__) первыми.
  const groups = useMemo(() => {
    const items = articlesQuery.data?.items ?? [];
    const map = new Map<string, WikiArticleListItem[]>();
    for (const c of categoriesQuery.data ?? []) if (!map.has(c.name)) map.set(c.name, []);
    for (const a of items) {
      const key = a.categoryName ?? '__root__';
      const arr = map.get(key) ?? [];
      arr.push(a);
      map.set(key, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return [...map.entries()].sort((x, y) => {
      if (x[0] === '__root__') return -1;
      if (y[0] === '__root__') return 1;
      // Ручной порядок: по sortOrder категории (drag сохраняет его), а где
      // sortOrder неизвестен — откат на номер в названии (см. sectionCmp).
      return sectionCmp(x[0], y[0]);
    });
  }, [articlesQuery.data, categoriesQuery.data, sectionCmp]);

  // Создание / удаление подраздела (категории).
  const createCatMut = useMutation({
    mutationFn: (name: string) => wikiAdminApi.createCategory(code as string, name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wiki', 'categories', code] });
      message.success('Подраздел создан');
    },
    onError: (e: Error) => message.error(e.message),
  });
  const deleteCatMut = useMutation({
    mutationFn: (id: string) => wikiAdminApi.deleteCategory(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wiki', 'categories', code] });
      message.success('Подраздел удалён');
    },
    onError: (e: Error) => message.error(e.message),
  });

  // Управление доступом подраздела по ролям (только kb:admin).
  const canAdmin = meQuery.data?.canAdmin ?? false;
  const [accessCategory, setAccessCategory] = useState<{ id: string; name: string; visibleToRoles: string[] } | null>(null);
  // Модалка создания подраздела (вместо window.prompt — аккуратный UI).
  const [subsectionOpen, setSubsectionOpen] = useState(false);
  const [subsectionName, setSubsectionName] = useState('');
  const addSubsection = (): void => {
    setSubsectionName('');
    setSubsectionOpen(true);
  };
  const submitSubsection = (): void => {
    const name = subsectionName.trim();
    if (!name) return;
    createCatMut.mutate(name, {
      onSuccess: () => {
        setSubsectionOpen(false);
        setSubsectionName('');
      },
    });
  };

  const totalArticles = articlesQuery.data?.items.length ?? block?.articleCount ?? 0;

  // ── Drag-n-drop (режим редактирования) ──
  // Статьи: реордер внутри группы + перенос МЕЖДУ подразделами (cat).
  // Категории: реордер подразделов (drag за ручку ⠿ в шапке секции).
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [catDrag, setCatDrag] = useState<string | null>(null); // перетаскиваемый подраздел
  const [overCat, setOverCat] = useState<string | null>(null); // подраздел под курсором (drop-цель)
  // Модалка «Новая статья» (быстрый диалог: подраздел + название → инлайн-редактор).
  const [newArticleOpen, setNewArticleOpen] = useState(false);
  const [newArticleCategory, setNewArticleCategory] = useState<string | undefined>(undefined);

  /** Список id статей категории в текущем порядке (по кэшу). */
  const groupIdsOf = (prev: ArticlesResponse, cat: string): string[] =>
    prev.items
      .filter((a) => (a.categoryName ?? '__root__') === cat)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((a) => a.id);

  /**
   * Дроп статьи: реордер внутри группы ИЛИ перенос в другой подраздел.
   * dropId=null → в конец целевой группы (дроп на шапку/пустую секцию).
   */
  const handleArticleDrop = async (
    targetCat: string,
    sourceId: string,
    dropId: string | null,
  ): Promise<void> => {
    const prev = qc.getQueryData<ArticlesResponse>(articlesKey);
    if (!prev) return;
    const src = prev.items.find((a) => a.id === sourceId);
    if (!src) return;
    const sourceCat = src.categoryName ?? '__root__';

    if (sourceCat === targetCat) {
      // ── Реордер внутри группы ──
      if (sourceId === dropId || dropId == null) return;
      const ids = groupIdsOf(prev, targetCat);
      const from = ids.indexOf(sourceId);
      const to = ids.indexOf(dropId);
      if (from < 0 || to < 0 || from === to) return;
      const reordered = [...ids];
      const [moved] = reordered.splice(from, 1);
      reordered.splice(to, 0, moved);
      const orderMap = new Map(reordered.map((id, i) => [id, i * 10]));
      const nextItems = prev.items.map((a) =>
        orderMap.has(a.id) ? { ...a, sortOrder: orderMap.get(a.id) as number } : a,
      );
      qc.setQueryData<ArticlesResponse>(articlesKey, { ...prev, items: nextItems });
      try {
        await editorApi.reorderArticles(reordered);
        void qc.invalidateQueries({ queryKey: articlesKey });
      } catch (e) {
        qc.setQueryData<ArticlesResponse>(articlesKey, prev);
        message.error(`Не удалось сохранить порядок: ${(e as Error).message}`);
      }
      return;
    }

    // ── Перенос в другой подраздел ──
    const targetIds = groupIdsOf(prev, targetCat).filter((id) => id !== sourceId);
    const at = dropId ? targetIds.indexOf(dropId) : targetIds.length;
    const insertAt = at < 0 ? targetIds.length : at;
    targetIds.splice(insertAt, 0, sourceId);

    const newCatName = targetCat === '__root__' ? null : targetCat;
    const orderMap = new Map(targetIds.map((id, i) => [id, i * 10]));
    const nextItems = prev.items.map((a) =>
      a.id === sourceId
        ? { ...a, categoryName: newCatName, sortOrder: (orderMap.get(a.id) ?? 0) }
        : orderMap.has(a.id)
          ? { ...a, sortOrder: orderMap.get(a.id) as number }
          : a,
    );
    qc.setQueryData<ArticlesResponse>(articlesKey, { ...prev, items: nextItems });

    const targetCatId = targetCat === '__root__' ? null : (catMeta.get(targetCat)?.id ?? null);
    try {
      await editorApi.moveArticle(sourceId, targetCatId, targetIds);
      void qc.invalidateQueries({ queryKey: articlesKey });
    } catch (e) {
      qc.setQueryData<ArticlesResponse>(articlesKey, prev);
      message.error(`Не удалось перенести: ${(e as Error).message}`);
    }
  };

  /** Дроп подраздела: переставить категории (sourceCat перед targetCat). */
  const handleCategoryDrop = async (sourceCat: string, targetCat: string): Promise<void> => {
    if (sourceCat === targetCat || sourceCat === '__root__' || targetCat === '__root__') return;
    const cats = (categoriesQuery.data ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);
    const ids = cats.map((c) => c.id);
    const fromId = catMeta.get(sourceCat)?.id;
    const toId = catMeta.get(targetCat)?.id;
    if (!fromId || !toId) return;
    const from = ids.indexOf(fromId);
    const to = ids.indexOf(toId);
    if (from < 0 || to < 0 || from === to) return;
    const reordered = [...ids];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    // Оптимистично переставляем sortOrder категорий в кэше.
    const orderMap = new Map(reordered.map((id, i) => [id, i]));
    const prevCats = categoriesQuery.data;
    qc.setQueryData(['wiki', 'categories', code], (old: typeof prevCats) =>
      (old ?? []).map((c) => ({ ...c, sortOrder: orderMap.get(c.id) ?? c.sortOrder })),
    );
    try {
      await wikiAdminApi.reorderCategories(reordered);
      void qc.invalidateQueries({ queryKey: ['wiki', 'categories', code] });
    } catch (e) {
      qc.setQueryData(['wiki', 'categories', code], prevCats);
      message.error(`Не удалось переставить подразделы: ${(e as Error).message}`);
    }
  };

  // ── Авто-скролл при подведении к верхнему/нижнему краю окна во время drag ──
  useEffect(() => {
    const dragging = dragId != null || catDrag != null;
    if (!dragging) return;
    let raf = 0;
    let speed = 0;
    const tick = (): void => {
      if (speed !== 0) window.scrollBy(0, speed);
      raf = window.requestAnimationFrame(tick);
    };
    const onDragOver = (e: DragEvent): void => {
      const y = e.clientY;
      const h = window.innerHeight;
      const zone = 90; // px от края
      if (y < zone) speed = -Math.ceil((zone - y) / 6);
      else if (y > h - zone) speed = Math.ceil((y - (h - zone)) / 6);
      else speed = 0;
    };
    window.addEventListener('dragover', onDragOver);
    raf = window.requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.cancelAnimationFrame(raf);
      speed = 0;
    };
  }, [dragId, catDrag]);

  // ── HUB 2.0: выбор подраздела слева + чипы типов справа ──
  // null — «Все статьи» (плоский список в порядке подразделов).
  const [selectedCat, setSelectedCat] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<string | null>(null);
  // Подраздел мог исчезнуть (удалили/переименовали) — тогда назад к «Все статьи».
  useEffect(() => {
    if (selectedCat && !groups.some(([c]) => c === selectedCat)) setSelectedCat(null);
  }, [groups, selectedCat]);

  const hasRoot = groups.some(([c]) => c === '__root__');
  const catLabel = (cat: string): string => {
    if (cat === '__root__') return 'Основные статьи';
    // Авто-нумерация по позиции (drag): цифру в названии писать не нужно.
    const gi = groups.findIndex(([c]) => c === cat);
    return `${gi - (hasRoot ? 1 : 0) + 1}. ${cat.replace(/^\s*\d+\.\s*/, '')}`;
  };
  const visibleItems = useMemo(() => {
    const base = selectedCat ? (groups.find(([c]) => c === selectedCat)?.[1] ?? []) : groups.flatMap(([, items]) => items);
    return activeType ? base.filter((a) => a.type === activeType) : base;
  }, [groups, selectedCat, activeType]);
  // Чипы — только типы, которые реально есть в разделе (без мёртвых фильтров).
  const typesInBlock = useMemo(() => new Set((articlesQuery.data?.items ?? []).map((a) => a.type)), [articlesQuery.data]);
  const typeChips = TYPE_CHIPS.filter((c) => c.type === null || typesInBlock.has(c.type));
  const selectedMeta = selectedCat && selectedCat !== '__root__' ? catMeta.get(selectedCat) : undefined;

  return (
    <div>
      <Link to="/knowledge" className="fk-kb-back">
        <ArrowLeftOutlined /> База знаний
      </Link>

      {blocksQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 1 }} style={{ marginTop: 12 }} />
      ) : !block ? (
        <Empty description={`Раздел «${code}» не найден`} style={{ marginTop: 24 }} />
      ) : (
        <div className="fk-kb-blockhead">
          <span className="fk-kb-blockhead-icon" style={{ background: sectionSoft(block.color), color: sectionAccent(block.color) }}>
            {renderSectionIcon(block.icon)}
          </span>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            <h1 className="fk-page-title" style={{ margin: 0 }}>{block.name}</h1>
            <div className="fk-page-sub" style={{ marginTop: 2 }}>
              {[(block as KbBlock).description?.trim(), `${fmtInt(totalArticles)} ${plural(totalArticles, 'статья', 'статьи', 'статей')}`, groups.length > 1 ? `${groups.length} ${plural(groups.length, 'подраздел', 'подраздела', 'подразделов')}` : null]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {canAdmin && selectedMeta && selectedCat && (
              <Button
                variant="ghost"
                icon={<LockOutlined />}
                onClick={() => setAccessCategory({ id: selectedMeta.id, name: selectedCat, visibleToRoles: selectedMeta.visibleToRoles })}
                title="Кому виден выбранный подраздел"
              >
                Доступ
              </Button>
            )}
            {canEditMode && (
              <Button variant={editMode ? 'soft' : 'ghost'} icon={editMode ? <CheckOutlined /> : <EditOutlined />} onClick={() => setEditMode((v) => !v)}>
                {editMode ? 'Готово' : 'Порядок'}
              </Button>
            )}
            {canCreate && code && (
              <Button
                variant="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setNewArticleCategory(selectedCat && selectedCat !== '__root__' ? selectedCat : undefined);
                  setNewArticleOpen(true);
                }}
              >
                Добавить статью
              </Button>
            )}
          </div>
        </div>
      )}

      {editMode && block && (
        <div className="fk-kb-hint">
          <InfoCircleOutlined />
          Тащите статьи за ⠿: на другую статью — встанет перед ней, на подраздел слева — переедет в него. Подразделы слева
          переставляются так же. Порядок сохраняется сразу.
        </div>
      )}

      <div className="fk-kb-block-grid">
        {/* Подразделы */}
        <aside className="fk-kb-panel fk-kb-cats" aria-label="Подразделы">
          <button type="button" className="fk-kb-cat" aria-pressed={selectedCat === null} onClick={() => setSelectedCat(null)}>
            <span className="fk-kb-cat-name" style={{ fontWeight: 700 }}>Все статьи</span>
            <span className="fk-kb-mono">{totalArticles}</span>
          </button>
          {groups.map(([cat, items]) => {
            const meta = catMeta.get(cat);
            const restricted = cat !== '__root__' && (meta?.visibleToRoles.length ?? 0) > 0;
            const isDrop = editMode && overCat === cat && ((catDrag != null && catDrag !== cat) || dragId != null);
            return (
              <button
                key={cat}
                type="button"
                className={`fk-kb-cat${isDrop ? ' is-drop' : ''}`}
                aria-pressed={selectedCat === cat}
                onClick={() => setSelectedCat(cat)}
                draggable={editMode && cat !== '__root__'}
                onDragStart={(e) => {
                  if (!editMode || cat === '__root__') return;
                  setCatDrag(cat);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData(DND_CAT_MIME, cat);
                }}
                onDragOver={(e) => {
                  if (!editMode) return;
                  const t = e.dataTransfer.types;
                  if (t.includes(DND_CAT_MIME) || t.includes(DND_MIME)) {
                    e.preventDefault();
                    if (overCat !== cat) setOverCat(cat);
                  }
                }}
                onDragLeave={() => {
                  if (overCat === cat) setOverCat(null);
                }}
                onDrop={(e) => {
                  if (!editMode) return;
                  const t = e.dataTransfer.types;
                  e.preventDefault();
                  if (t.includes(DND_CAT_MIME)) {
                    const srcCat = e.dataTransfer.getData(DND_CAT_MIME);
                    if (srcCat) void handleCategoryDrop(srcCat, cat);
                  } else if (t.includes(DND_MIME)) {
                    const srcId = e.dataTransfer.getData(DND_MIME);
                    if (srcId) void handleArticleDrop(cat, srcId, null);
                  }
                  setOverCat(null);
                  setCatDrag(null);
                  setDragId(null);
                }}
                onDragEnd={() => {
                  setCatDrag(null);
                  setOverCat(null);
                }}
              >
                <span className="fk-kb-cat-name">
                  {editMode && cat !== '__root__' ? (
                    <span className="fk-kb-grip">
                      <HolderOutlined />
                    </span>
                  ) : (
                    <FolderOutlined style={{ color: 'var(--fk-text-muted)' }} />
                  )}
                  <span title={catLabel(cat)}>{catLabel(cat)}</span>
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  {restricted && (
                    <Tooltip title={`Доступен ${meta!.visibleToRoles.length} ${pluralRoles(meta!.visibleToRoles.length)} (остальным скрыт)`}>
                      <LockOutlined style={{ color: 'var(--fk-orange)', fontSize: 12 }} />
                    </Tooltip>
                  )}
                  {editMode && cat !== '__root__' && items.length === 0 && meta && (
                    <span
                      role="button"
                      tabIndex={0}
                      title="Удалить пустой подраздел"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Удалить пустой подраздел «${cat}»?`)) deleteCatMut.mutate(meta.id);
                      }}
                      style={{ color: 'var(--fk-error)', display: 'inline-flex' }}
                    >
                      <DeleteOutlined />
                    </span>
                  )}
                  <span className="fk-kb-mono">{items.length}</span>
                </span>
              </button>
            );
          })}
          {canCreate && (
            <button type="button" className="fk-kb-cat-add" disabled={createCatMut.isPending} onClick={addSubsection}>
              + Подраздел
            </button>
          )}
        </aside>

        {/* Статьи */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
          <div className="fk-kb-chips">
            {typeChips.map((c) => (
              <Chip key={c.label} active={c.type === activeType} onClick={() => setActiveType(c.type)}>
                {c.label}
              </Chip>
            ))}
            <span style={{ flex: 1 }} />
            <span className="fk-kb-muted" style={{ fontSize: 13 }}>
              {visibleItems.length} {plural(visibleItems.length, 'статья', 'статьи', 'статей')}
            </span>
          </div>
          {articlesQuery.isLoading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : articlesQuery.error ? (
            <Alert type="error" showIcon message="Не удалось загрузить статьи" description={(articlesQuery.error as Error).message} />
          ) : visibleItems.length === 0 ? (
            <div className="fk-kb-panel fk-kb-empty">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={activeType ? 'Статей такого типа здесь нет' : selectedCat ? 'В подразделе пока нет статей' : 'В этом разделе пока нет статей'}
              />
              {canCreate && code && (
                <Button
                  variant="soft"
                  icon={<FileAddOutlined />}
                  onClick={() => {
                    setNewArticleCategory(selectedCat && selectedCat !== '__root__' ? selectedCat : undefined);
                    setNewArticleOpen(true);
                  }}
                >
                  Добавить статью
                </Button>
              )}
            </div>
          ) : (
            <div className="fk-kb-acards">
              {visibleItems.map((a) => (
                <ArticleCard
                  key={a.id}
                  article={a}
                  editMode={editMode}
                  isDragging={dragId === a.id}
                  showDropLine={!!dragId && dragId !== a.id && overId === a.id}
                  onDragStart={() => setDragId(a.id)}
                  onDragOverRow={() => {
                    if (overId !== a.id) setOverId(a.id);
                  }}
                  onDropRow={(sourceId) => {
                    void handleArticleDrop(a.categoryName ?? '__root__', sourceId, a.id);
                  }}
                  onDragEnd={() => {
                    setDragId(null);
                    setOverId(null);
                  }}
                  onNavigate={() => navigate(`/article/${a.slug}`)}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {accessCategory && <CategoryAccessDialog key={accessCategory.id} category={accessCategory} onClose={() => setAccessCategory(null)} />}
      {code && (
        <NewArticleModal open={newArticleOpen} onClose={() => setNewArticleOpen(false)} blockCode={code} defaultCategory={newArticleCategory} />
      )}

      <Modal
        title="Новый подраздел"
        open={subsectionOpen}
        onCancel={() => setSubsectionOpen(false)}
        onOk={submitSubsection}
        okText="Создать"
        cancelText="Отмена"
        okButtonProps={{ disabled: !subsectionName.trim(), loading: createCatMut.isPending }}
        destroyOnHidden
      >
        <div style={{ fontSize: 13, color: 'var(--fk-text-muted)', marginBottom: 6, marginTop: 8 }}>Название подраздела</div>
        <Input
          value={subsectionName}
          onChange={(e) => setSubsectionName(e.target.value)}
          placeholder="Например: Оборудование"
          autoFocus
          maxLength={120}
          onPressEnter={submitSubsection}
        />
      </Modal>
    </div>
  );
}

/**
 * Карточка статьи раздела (прототип «block»): иконка типа, название, анонс,
 * «подраздел · владелец · обновлено · N мин», справа — статус и просмотры.
 * В режиме порядка — draggable (ручка ⠿), клик всё равно открывает статью.
 */
function ArticleCard({
  article,
  editMode,
  isDragging,
  showDropLine,
  onDragStart,
  onDragOverRow,
  onDropRow,
  onDragEnd,
  onNavigate,
}: {
  article: WikiArticleListItem;
  editMode: boolean;
  isDragging: boolean;
  showDropLine: boolean;
  onDragStart: () => void;
  onDragOverRow: () => void;
  onDropRow: (sourceId: string) => void;
  onDragEnd: () => void;
  onNavigate: () => void;
}): JSX.Element {
  const a = article as KbArticleListItem;
  const meta = [
    a.categoryName ? a.categoryName.replace(/^\s*\d+\.\s*/, '') : null,
    a.ownerName,
    kbShortDate(a.updatedAt ?? a.publishedAt),
    a.readingMinutes ? `${a.readingMinutes} мин` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const status = articleStatusTag(a);
  const inner = (
    <>
      <span className="fk-kb-tile" style={{ fontSize: a.icon ? 20 : 18 }}>
        {editMode ? <HolderOutlined /> : a.icon || materialIcon(a.type)}
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="fk-kb-acard-title">{a.title}</div>
        {(a.excerpt ?? '').trim() && <div className="fk-kb-acard-ex">{a.excerpt}</div>}
        <div className="fk-kb-acard-meta">{meta || (MATERIAL_LABELS[a.type] ?? 'Статья')}</div>
      </div>
      <div className="fk-kb-acard-side">
        <Tag color={status.color}>{status.label}</Tag>
        {typeof a.viewCount === 'number' && (
          <span className="fk-kb-acard-views" title="Просмотры">
            <EyeOutlined /> {fmtInt(a.viewCount)}
          </span>
        )}
      </div>
    </>
  );
  const cls = `fk-kb-acard${isDragging ? ' is-drag' : ''}${showDropLine ? ' is-drop' : ''}`;
  if (!editMode) {
    return (
      <Link to={`/article/${a.slug}`} className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <div
      className={cls}
      role="button"
      tabIndex={0}
      draggable
      onClick={onNavigate}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onNavigate();
      }}
      onDragStart={(e) => {
        onDragStart();
        e.dataTransfer.effectAllowed = 'move';
        // id статьи — в dataTransfer (drop читает отсюда, а не из ещё-не-закоммиченного state).
        e.dataTransfer.setData(DND_MIME, a.id);
        e.dataTransfer.setData('text/plain', a.id);
      }}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes(DND_MIME)) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          onDragOverRow();
        }
      }}
      onDrop={(e) => {
        if (e.dataTransfer.types.includes(DND_MIME)) {
          e.preventDefault();
          const sourceId = e.dataTransfer.getData(DND_MIME);
          if (sourceId && sourceId !== a.id) onDropRow(sourceId);
        }
        onDragEnd();
      }}
      onDragEnd={onDragEnd}
      style={{ cursor: 'grab' }}
    >
      {inner}
    </div>
  );
}

/** Статус карточки: обязательное к ознакомлению → ознакомлен → статус публикации. */
function articleStatusTag(a: WikiArticleListItem): { color: TagColor; label: string } {
  if (a.requiresAck && !a.isAcked && a.status === 'PUBLISHED') return { color: 'red', label: 'Обязательно' };
  if (a.requiresAck && a.isAcked) return { color: 'green', label: 'Ознакомлен' };
  switch (a.status) {
    case 'REVIEW':
      return { color: 'gold', label: 'На согласовании' };
    case 'DRAFT':
      return { color: 'default', label: 'Черновик' };
    case 'ARCHIVED':
      return { color: 'default', label: 'Архив' };
    default:
      return { color: 'green', label: 'Опубликовано' };
  }
}

// Для подсказки замка: «Доступен 1 роли / 2 ролям / 5 ролям».
function pluralRoles(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'роли';
  return 'ролям';
}
